<template>
  <div class="row" :class="{ 'd-none': item.displayOn && !display_none_check }">
    <template v-for="(item, key) in form">
      <div
        class="form-group mb-0"
        :class="`col-${item.size} ${item.class}`"
        :key="key"
        v-if="
          shouldRenderComponent(item.type, key) && getComponentName(item.type)
        "
      >
        <component
          :is="getComponentName(item.type)"
          :item="item"
          :item_key="key"
          :item_value="data[key]"
          :selectedItem="selectedItem"
          :last_updated="last_updated"
          :submitted="submitted"
          :validation="$v.data[key]"
          :updateValue="updateValue"
          :topbarControls="topbarControls"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import _ from "lodash";

const componentsMap = {
  patientAssistChat: () => import("./PatientAssistChat.vue"),
  patientVoiceChat: () => import("./PatientVoiceChat.vue"),
  hr: () => import("./HR.vue"),
  gutter: () => import("./gutter.vue"),
  heading: () => import("./Heading.vue"),
  image: () => import("./ImageIcon.vue"),
  footerImage: () => import("./FooterImageIcon.vue"),
  text: () => import("./TextInput.vue"),
  imgPreview: () => import("./ImagePreview.vue"),
  externalText: () => import("./ExternalTextInput.vue"),
  vidText: () => import("./VidTextInput.vue"),
  imgSelect: () => import("../partials/ImgSelectInput.vue"),
  pdfSelect: () => import("../partials/PdfSelectInput.vue"),
  imgSelectHide: () => import("../partials/ImgSelectInputHide.vue"),
  iconSelect: () => import("../partials/IconSelectInput.vue"),
  subgroup: () => import("./SubgroupInput.vue"),
  stafflist: () => import("./StaffInput.vue"),
  variableList: () => import("./VariableListInput.vue"),
  variableListSingle: () => import("./VariableListInputSingle.vue"),
  schedulerinput: () => import("./SchedulerInput.vue"),
  contactinput: () => import("./ContactInput.vue"),
  slug: () => import("./SlugInput.vue"),
  preview: () => import("./Preview.vue"),
  recommendedAppsPreview: () => import("./RecommendedAppsPreview.vue"),
  bannersPreview: () => import("./BannersPreview.vue"),
  optionPreview: () => import("./OptionPreview.vue"),
  practiceInfoPreview: () => import("./PracticeInfoPreview.vue"),
  manageConditionPreview: () => import("./ManageConditionPreview.vue"),
  managingConditionsPreview: () => import("./ManagingConditionsPreview.vue"),
  createGoogleMapBoundary: () => import("./CreateGoogleMapBoundary.vue"),
  webPreview: () => import("./WebPreview.vue"),
  tabletPreview: () => import("./TabletPreview.vue"),
  mobilePreview: () => import("./MobilePreview.vue"),
  ht_days: () => import("./HTDays.vue"),
  time: () => import("./TimeSelectInput.vue"),
  date: () => import("./DateInput.vue"),
  checkbox: () => import("./CheckboxInput.vue"),
  mailSettingsCheckbox: () => import("./MailSettingsCheckboxInput.vue"),
  select: () => import("./SelectInput.vue"),
  selectObject: () => import("./SelectInputObject.vue"),
  subgroupSelect: () => import("../partials/SubGroupSelectInput.vue"),
  groupSelect: () => import("../partials/GroupSelectInput.vue"),
  groupSelectClinics: () => import("../partials/GroupSelectClinicsInput.vue"),
  footerGroupSelect: () => import("../partials/FooterGroupSelectInput.vue"),
  reGroupSelect: () => import("../partials/REGroupSelectInput.vue"),
  locationSelect: () => import("../partials/LocationSelectInput.vue"),
  locationMultiSelect: () => import("../partials/LocationMultiSelectInput.vue"),
  practiceMultiSelect: () => import("../partials/PracticeMultiSelectInput.vue"),
  newsSelect: () => import("../partials/NewsSelectInput.vue"),
  questionSelect: () => import("../partials/QuestionSelectInput.vue"),
  checkObject: () => import("./CheckObjectInput.vue"),
  checkObjectMulti: () => import("./CheckObjectMultiInput.vue"),
  checkObjectMultiLoc: () => import("./CheckObjectMultiLocInput.vue"),
  checkObjectMultiLocPlaces: () =>
    import("./CheckObjectMultiLocPlacesInput.vue"),
  selectObjectMultiLocPlaces: () =>
    import("./SelectObjectMultiLocPlacesInput.vue"),
  checkObjectMultiOptions: () => import("./CheckObjectMultiOptionsInput.vue"),
  checkObjectMultiAilments: () => import("./CheckObjectMultiAilmentsInput.vue"),
  radioObject: () => import("./RadioObjectInput.vue"),
  radioObjectwithImg: () => import("./RadioObjectwithImgInput.vue"),
  radio: () => import("./RadioInput.vue"),
  alertinput: () => import("./AlertInput.vue"),
  toggle: () => import("./ToggleInput.vue"),
  ckeditor: () => import("./CKEditorInput.vue"),
};

export default {
  name: "FormSections",
  components: {
    ...componentsMap,
  },
  data() {
    return {
      componentsMap,
    };
  },
  methods: {
    getComponentName(type) {
      return this.componentsMap[type];
    },
    shouldRenderComponent(type, key) {
      if (
        [
          "patientAssistChat",
          "patientVoiceChat",
          "hr",
          "gutter",
          "heading",
          "image",
          "footerImage",
          "preview",
          "recommendedAppsPreview",
          "bannersPreview",
          "optionPreview",
          "practiceInfoPreview",
          "managingConditionsPreview",
          "webPreview",
          "tabletPreview",
          "mobilePreview",
        ].includes(type)
      ) {
        return Object.entries(this.data).length;
      } else if (
        [
          "text",
          "imgPreview",
          "externalText",
          "vidText",
          "imgSelect",
          "pdfSelect",
          "imgSelectHide",
          "iconSelect",
          "subgroup",
          "stafflist",
          "variableList",
          "variableListSingle",
          "schedulerinput",
          "contactinput",
          "slug",
          "ht_days",
          "time",
          "date",
          "checkbox",
          "mailSettingsCheckbox",
          "select",
          "selectObject",
          "subgroupSelect",
          "groupSelect",
          "groupSelectClinics",
          "footerGroupSelect",
          "reGroupSelect",
          "locationSelect",
          "locationMultiSelect",
          "practiceMultiSelect",
          "newsSelect",
          "questionSelect",
          "checkObject",
          "checkObjectMulti",
          "checkObjectMultiLoc",
          "checkObjectMultiLocPlaces",
          "selectObjectMultiLocPlaces",
          "checkObjectMultiOptions",
          "checkObjectMultiAilments",
          "radioObject",
          "radioObjectwithImg",
          "radio",
          "alertinput",
          "toggle",
          "ckeditor",
        ].includes(type)
      ) {
        return this.$v.data[key].$model != undefined;
      } else {
        return false;
      }
    },
  },
  validations() {
    let validations = new Object();
    validations.data = new Object();
    for (const [key, value] of Object.entries(this.form)) {
      if (value.required) {
        validations.data[key] = { required };
      } else {
        validations.data[key] = {};
      }
    }
    return validations;
  },
  computed: {
    display_none_check() {
      let displayon = true;
      if (this.item.displayOn && "displayOnValue" in this.item) {
        let val = this.selectedItem[this.item.displayOn];
        let val2 = this.item.displayOnValue;
        if (typeof val === "object") {
          displayon = _.isEqual(val, val2);
        } else {
          displayon = this.selectedItem
            ? this.selectedItem[this.item.displayOn]
            : false;
        }
      }
      return displayon;
    },
  },
  watch: {
    last_updated: {
      handler(val) {
        console.log("val.value", val.value, this.item.displayOn, val.key);
        if (val && this.item.displayOn && val.key == this.item.displayOn) {
          this.display_none_check =
            JSON.stringify(val.value) == this.item.displayOnValue
              ? true
              : false;
        }
      },
      deep: true,
    },
    display_none_check(val) {
      this.item.display = this.item.displayOn && !val ? false : true;
    },
  },
  props: {
    item_key: {
      type: String,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
    topbarControls: {
      type: Object,
      required: false,
    },
    vmodel: {
      type: Object,
      required: false,
    },
    validation: {
      type: Object,
      required: true,
    },
    validation_data: {
      type: Object,
      required: false,
    },
    selectedItem: {
      type: Object,
      required: false,
    },
    setSelectedItems: {
      type: Function,
      required: false,
    },
    updateSelectedItem: {
      type: Function,
      required: false,
    },
    closeModal: {
      type: Function,
      required: false,
    },
    updateValue: {
      type: Function,
      required: true,
    },
    submitted: {
      type: Boolean,
      required: true,
    },
    last_updated: {
      type: Object,
      required: false,
    },
  },
};
</script>

<style scoped>
/* Add your styles here */
</style>
